// Yoj hero: <yoj-carousel>, an accessible slideshow built on native scroll snapping. Swiping and trackpads work
// natively; buttons and dots move between slides; slides out of view are inert, so keyboard users never land on a
// hidden link. Autoplay, if switched on, pauses on hover and focus, has a pause button, and never runs for visitors
// who ask for reduced motion. In the theme editor, selecting a slide shows it.

function defineElement() {
  if (typeof customElements === 'undefined' || customElements.get('yoj-carousel')) return;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class YojCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ref="track"]');
      this.slides = [...this.querySelectorAll('[data-ref="slide"]')];
      this.dots = [...this.querySelectorAll('[data-ref="dot"]')];
      this.pauseButton = this.querySelector('[data-ref="pause"]');
      this.index = 0;
      if (!this.track || this.slides.length < 2) return;

      this.querySelector('[data-ref="prev"]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-ref="next"]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
      this.addEventListener('keydown', (event) => {
        if (!event.target.closest('[data-ref="controls"]')) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1, true); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1, true); }
      });

      // The slide most in view is the current one, however it got there (button, swipe, trackpad, keyboard)
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) if (entry.isIntersecting) this.setCurrent(this.slides.indexOf(entry.target));
      }, { root: this.track, threshold: 0.6 });
      this.slides.forEach((slide) => this.observer.observe(slide));
      this.setCurrent(0);

      // Autoplay: starts paused for visitors who ask for reduced motion, who can still press play
      this.interval = Math.max(3, Number(this.dataset.interval) || 6) * 1000;
      this.userPaused = reducedMotion();
      this.hovering = false;
      this.focused = false;
      // Focus inside holds it still, but pressing play means play, even though the button keeps the focus
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (!this.userPaused) this.focused = false;
        this.updatePlay();
      });
      this.addEventListener('pointerenter', () => { this.hovering = true; this.updatePlay(); });
      this.addEventListener('pointerleave', () => { this.hovering = false; this.updatePlay(); });
      this.addEventListener('focusin', () => { this.focused = true; this.updatePlay(); });
      this.addEventListener('focusout', (event) => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.updatePlay(); } });
      this.onVisibility = () => this.updatePlay();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.updatePlay();

      // Theme editor: show the slide being edited, and stop autoplay while it's selected
      this.onBlockSelect = (event) => {
        const i = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
        if (i >= 0) { this.editing = true; this.go(i, false); this.updatePlay(); }
      };
      this.onBlockDeselect = () => { this.editing = false; this.updatePlay(); };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    go(index, fromUser) {
      const count = this.slides.length;
      const target = ((index % count) + count) % count;
      // The track is the slides' offset parent (it's positioned), so offsetLeft is the scroll position needed
      this.track.scrollTo({ left: this.slides[target].offsetLeft, behavior: reducedMotion() ? 'auto' : 'smooth' });
      this.setCurrent(target);
      if (fromUser && this.timer) { this.stop(); this.start(); }
    }

    setCurrent(index) {
      if (index < 0) return;
      this.index = index;
      // Inert takes slides out of view out of the keyboard order and away from screen readers
      this.slides.forEach((slide, i) => { slide.inert = i !== index; });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }

    updatePlay() {
      const wanted = this.dataset.autoplay === 'true' && !this.userPaused && !this.hovering && !this.focused && !this.editing && !document.hidden;
      if (wanted) this.start(); else this.stop();
      if (this.pauseButton) {
        this.pauseButton.setAttribute('aria-label', this.userPaused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
        this.pauseButton.dataset.state = this.userPaused ? 'paused' : 'playing';
      }
      // Announce slide changes only while nothing moves on its own
      this.track.setAttribute('aria-live', wanted ? 'off' : 'polite');
    }

    start() {
      if (this.timer) return;
      this.timer = setInterval(() => this.go(this.index + 1, false), this.interval);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('yoj-carousel', YojCarousel);
}

defineElement();
